/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags';
import { type SuggestionItemResponse } from '@atlassian/navigation-bff-data-provider';
import { ATI_PRESETS, type UniversalCreateSourceProduct } from '@atlassian/universal-create-common';
import { withLoomVideoConfig } from '@atlassian/universal-create-integrations/loom';

import { GenericErrorBoundary } from '../../../services';
import type { SuggestionConfigContext } from '../../../types';
import { getCreateContentLoader } from '../../integrations/forms/formLoader';

import { LinkItem } from './primitives/LinkItem/LinkItem';
import { LoomVideoItem as SuggestionItemLoomVideoItem } from './primitives/LoomItem/LoomVideoItem';
import { SuggestionItemBase, type SuggestionItemBaseProps } from './SuggestionItemBase';

export interface SuggestionItemProps {
	/**
	 * Additional context for the component
	 */
	context?: SuggestionConfigContext;

	/**
	 * The index of this item within the suggestion section (0-based)
	 */
	itemIndex: number;

	/**
	 * The source product context
	 */
	product: UniversalCreateSourceProduct;

	/**
	 * The response item from Universal Create
	 */
	responseItem: SuggestionItemResponse;

	/**
	 * The sub-product context
	 */
	subProduct?: string;

	/**
	 * The total count of items in the suggestion section
	 */
	totalItemsCount: number;
}

interface LoomVideoConfigProps extends SuggestionItemBaseProps {
	context: SuggestionConfigContext;
	product: SuggestionItemProps['product'];
	subProduct?: SuggestionItemProps['subProduct'];
}

function renderPlatformSuggestionItem({
	responseItem,
	itemIndex,
	totalItemsCount,
	context,
	product,
	subProduct,
}: {
	context: SuggestionConfigContext;
	itemIndex: number;
	product: UniversalCreateSourceProduct;
	responseItem: SuggestionItemResponse;
	subProduct?: string;
	totalItemsCount: number;
}) {
	switch (responseItem.ati) {
		case ATI_PRESETS.LOOM: {
			const LoomVideoItem = fg('navx-2470-migrate-loom-integration')
				? withLoomVideoConfig<LoomVideoConfigProps>(SuggestionItemBase)
				: SuggestionItemLoomVideoItem;

			return (
				<LoomVideoItem
					responseItem={responseItem}
					context={context}
					product={product}
					subProduct={subProduct}
					itemIndex={itemIndex}
					totalItemsCount={totalItemsCount}
				/>
			);
		}
		case ATI_PRESETS.GOAL:
		case ATI_PRESETS.PROJECT:
		case ATI_PRESETS.TEAM: {
			if (!fg('navx_uc_platformisation_forms_enabled')) {
				return null; // should not show this item if the feature flag is off
			}

			const loadCreateContent = getCreateContentLoader(responseItem.ati);

			const customisedItemConfig = {
				loadCreateContent,
				onClickCloseMenu: !loadCreateContent,
			};

			return (
				<SuggestionItemBase
					responseItem={responseItem}
					itemIndex={itemIndex}
					totalItemsCount={totalItemsCount}
					customisedItemConfig={customisedItemConfig}
				/>
			);
		}
		default:
			return undefined;
	}
}

function renderSuggestionItem({
	responseItem,
	itemIndex,
	totalItemsCount,
	context,
	product,
	subProduct,
}: {
	context: SuggestionConfigContext;
	itemIndex: number;
	product: UniversalCreateSourceProduct;
	responseItem: SuggestionItemResponse;
	subProduct?: string;
	totalItemsCount: number;
}) {
	switch (responseItem.type) {
		case 'platform':
			return renderPlatformSuggestionItem({
				responseItem,
				itemIndex,
				totalItemsCount,
				context,
				product,
				subProduct,
			});
		case 'link':
			return (
				// eslint-disable-next-line @atlassian/a11y/anchor-is-valid, @atlassian/a11y/anchor-has-content -- See https://go/a11y-anchor-has-content for more details
				<LinkItem
					responseItem={responseItem}
					context={context}
					product={product}
					itemIndex={itemIndex}
					totalItemsCount={totalItemsCount}
				/>
			);
		default:
			break;
	}

	return undefined;
}

function SuggestionItemInner({
	responseItem,
	itemIndex,
	totalItemsCount,
	context,
	product,
	subProduct,
}: SuggestionItemProps) {
	if (context) {
		const renderedItem = renderSuggestionItem({
			responseItem,
			itemIndex,
			totalItemsCount,
			context,
			subProduct,
			product,
		});

		if (renderedItem !== undefined) {
			return renderedItem;
		}
	}

	// Return general suggestion item with no customisedItemConfig
	return (
		<SuggestionItemBase
			responseItem={responseItem}
			itemIndex={itemIndex}
			totalItemsCount={totalItemsCount}
		/>
	);
}

/**
 * A component that renders a suggestion item.
 * Wrapped by the ErrorBoundary component to handle errors so that it won't break the whole menu.
 *
 * @param props - @see SuggestionItemProps
 * @returns - JSX.Element @see SuggestionItemInner
 */
export function SuggestionItem(props: SuggestionItemProps) {
	return (
		<GenericErrorBoundary name="suggestion-item">
			<SuggestionItemInner {...props} />
		</GenericErrorBoundary>
	);
}
